import { Status, StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { recordInformation } from "components/DetailPage/RecordInformation";
import { CurrencyAndExchangeRate, CurrencyAndExchangeRatePicker, ICurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import { CostCenterLabel } from "components/Removables/RemovableCostCenter";
import { BankAccountSmallDetails, BookkeepingSmallDetails, CompanySmallDetails, ContactSmallDetails } from "components/SmallDetails";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import moment from "moment";
import React from "react";
import { formatShortDate, getCurrency, isNullOrEmptyString } from "utilities";
import { DocumentDueDate } from "../../components/Document/DocumentDueDate";
import { DocumentSendDate } from "../../components/Document/DocumentSendDate";
import { SendDocumentButton } from "../../invoice/detail/SendDocumentButton";
import { useCustomQuery } from "utilities/hookutils";
import { useContact } from "utilities/api/contactQuery";
import { useModals } from "Modals/ModalsProvider";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import { ReverseChargeAdvice } from "../../invoice/detail/ReverseChargeAdvice";
import { DocumentNumber } from "../../components/Document/DocumentNumber";
import NoBankAccountsMessageBox from "components/MessageBox/NoBankAccountsMessageBox";
import Loading from "components/panels/loading";
import DocumentSendDatePicker from "components/Document/DocumentSendDatePicker";
import DocumentDueDatePicker from "components/Document/DocumentDueDatePicker";
import { DocumentV1Term } from "components/Document/DocumentV1Term";
import { BankAccountDropdownOrWarning } from "../../invoice/BankAccountDropdownOrWarning";
import ChooseContact from "components/AddresseePicker/ChooseContact";
import { downloadFile, getDocumentCoreApiExceptInvoice } from "utilities/apiUtils";
import { documentFormStateToUpdateDocumentInstruction, documentFormStateToUpdateInvoiceInstruction, DocumentV1FormState } from "./documentFormState";
import { DocumentTypeWithoutInvoice, DocumentTypeWithoutUnknown } from "utilities/documentUtils";
import DocumentLifeCyclePill from "components/Document/DocumentLifeCyclePill";
import ChooseLifecycle, { LifeCycleValue } from "./ChooseLifecycle";
import ArtistRidersDropdown from "components/dropdowns/reactselect/ArtistRiderDropdown";
import Axios from "axios";
import { ButtonText } from "components/Atoms/Button";
import { YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

interface Props {
  document: server.dto.DocumentV1 | server.dto.InvoiceV1;
  formState: DocumentV1FormState;
  products: server.dto.ProductsByParent;
  onChange: (document: DocumentV1FormState) => void;
  minSendDate: moment.Moment;
  maxSendDate: moment.Moment;
  refetchDocument: () => Promise<any>;
  refetchProducts: () => Promise<any>;
  euVatNumber: server.dto.EuVatNumber;
  reverseChargeAdvice?: server.dto.ReverseChangeAdvices;
  lastModifiedUser: server.dto.UserSmall;
  artistRiders: server.dto.ArtistRider[];
}

export const GenericDocumentV1DetailSection = (props: Props) => {
  const { document, formState, minSendDate, maxSendDate } = props;
  const modals = useModals();
  const isInvoice = "invoiceId" in document;
  const documentType = (isInvoice ? "invoice" : document.documentType) as DocumentTypeWithoutUnknown;

  const isLocked = document.locked === "locked";

  const invoice = document as server.dto.InvoiceV1;
  const bookkeeping = document.bookkeeping;

  const statuses = site.currentEnvironment.statuses[documentType as DocumentTypeWithoutUnknown];

  const hasMultipleStatuses = statuses.length > 1;

  const displayOgm = bookkeeping?.companyAdmCountry === "belgium";

  const updateDocument = (update: Partial<DocumentV1FormState>) => {
    const nextState = { ...formState, ...update };
    if (isInvoice) {
      return window.CoreApi.Invoice.Update(documentFormStateToUpdateInvoiceInstruction(nextState));
    }
    const documentApi = getDocumentCoreApiExceptInvoice(documentType as DocumentTypeWithoutInvoice);
    return documentApi.Update(documentFormStateToUpdateDocumentInstruction(nextState));
  };

  const bankAccountsApi = useCustomQuery({
    queryFn: () => window.CoreApi.BankAccount.ListBankAccountSmall({
      bookkeepings: [bookkeeping.bookkeepingId],
      page: 1,
      perPage: 100
    }).then(r => r.data.data),
    queryKey: ["bankAccounts", { bookkeepingId: bookkeeping.bookkeepingId }],
    initialData: [],
    enabled: isInvoice,
  });

  const onValueChanged = async (key: string, value: any) => {
    if (key === "currencyAndExchangeRate") {
      props.onChange({ ...formState, currency: value.currency, exchangeRate: value.exchangeRate });
    } else if (key === "invoiceDate" || key === "sendDate") {
      props.onChange({
        ...formState,
        sendDate: value
      });
    } else if (key === "dueDate" || key === "paymentTerm") {
      props.onChange({
        ...formState,
        dueInDays: value
      });
    } else if (key === "addressee") {
      props.onChange({ ...formState, addresseeCompany: value, contact: undefined });
    } else if (key === "contact") {
      props.onChange({ ...formState, contact: value });
    } else if (key === "owner") {
      props.onChange({ ...formState, owner: value });
    } else if (key === "status") {
      props.onChange({ ...formState, status: value });
    } else if (key === "concerning") {
      props.onChange({ ...formState, subject: value });
    } else if (key === "lifeCycle") {
      const lifeCycle = value as LifeCycleValue;
      props.onChange({
        ...formState,
        lifeCycle: lifeCycle.lifeCycle,
        lifeCycleReason: lifeCycle.lifeCycle.value === "pending" ? null : lifeCycle.reason,
        completed: lifeCycle.lifeCycle.value === "pending" ? null : lifeCycle.completed
      });
    } else if (key === "bankAccount") {
      props.onChange({ ...formState, bankAccount: value });
    } else if (key === "costCenter") {
      props.onChange({ ...formState, costCenter: value });
    } else if (key === "artistRider") {
      props.onChange({ ...formState, artistRider: value });
    } else if (key === "draft") {
      props.onChange({ ...formState, lifeCycle: { value: "pending", text: "" } });
    }
    else {
      console.warn(`Unhandled key in GenericDocumentV1DetailSection: ${key}`);
      console.warn(`Value: ${value}`);
    }
  };

  const onSuccessfulUpdate = async (key: string, value: any) => {
    if (key === "addressee" && value != null) {
      const newCompany = value as server.dto.CompanySmall;
      modals.displayFinancialRiskModal(newCompany.companyId);
      await props.refetchDocument();
    } else if (key === "currencyAndExchangeRate") {
      await props.refetchProducts();
    } else if (key === "draft") {
      await props.refetchDocument();
    }
  };

  const downloadArtistRider = async () => {
    const fileName = `${formState.artistRider.title}.docx`;
    const fileBlobRes = await Axios.post(
      window.CoreApi.ArtistRider.DownloadFile.absoluteUrl(),
      { itemId: formState.artistRider.artistRiderId, fileName } as server.dto.DownloadTemplateFileInstruction,
      {
        responseType: "blob",
        withCredentials: true
      });
    downloadFile(fileBlobRes.data, fileName);
  }

  const contact = useContact(formState.contact);

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    isInPageEditable={document.permissions.canModify}
    sections={[
      {
        key: "documentMainDetails",
        type: "regular",
        title: site.currentEnvironment[documentType as DocumentTypeWithoutInvoice].singular,
        fields: [{
          key: "proforma",
          hidden: !isInvoice || formState.lifeCycle.value !== "draft",
          label: RESX.Document.ProForma,
          render: () => <SendDocumentButton
            updateDocument={updateDocument}
            canModify={document.permissions.canModify}
            onConverted={props.refetchDocument}
            convertTo="review"
          />,
        }, {
          key: "docNumber",
          hidden: false,
          label: RESX.GeneralLabels.Number,
          render: () => <DocumentNumber documentNumber={document.documentNumber} locked={document.locked} />,
        }, {
          key: "ogmCode",
          label: RESX.Financial.OgmCode,
          hidden: !isInvoice || formState.lifeCycle?.value === "draft" || !displayOgm,
          render: () => <div>{invoice.belgianOgmCode}</div>
        }, {
          key: "bookkeeping",
          label: RESX.Bookkeeping.resxBookkeeping,
          render: () => <BookkeepingSmallDetails bookkeeping={bookkeeping} />,
        }, {
          key: "draft",
          hidden: isInvoice || formState.lifeCycle.value !== "draft",
          label: RESX.DocumentLifeCycle.Draft,
          render: () => <YesNoText value={formState.lifeCycle.value === "draft"} />,
          inPageEdit: {
            render: ({ value, onChange }: RenderEditOptions<boolean>) => <div className="main-section-template-fields-padding">
              <Checkbox
                checked={value}
                onChange={(_, value) => onChange(value, true)}
              />
            </div>,
            type: "custom",
            initialValue: formState.lifeCycle.value === "draft",
            submitChange: async () => updateDocument({
              lifeCycle: {
                value: "pending",
                text: ""
              }
            })
          }
        }, {
          key: "lifeCycle",
          label: RESX.Document.Progress,
          hidden: isInvoice || formState.lifeCycle.value === "draft",
          render: () => <div className="flex-column gap-small">
            <div>
              <DocumentLifeCyclePill value={formState.lifeCycle.value} />
            </div>
            {formState.completed && <div>{RESX.DateTime.lblOn}: {formatShortDate(formState.completed)}</div>}
            {!isNullOrEmptyString(formState.lifeCycleReason) && <div>{RESX.GeneralLabels.Comment}: {formState.lifeCycleReason}</div>}
          </div>,
          inPageEdit: {
            render: ({ value, onChange }: RenderEditOptions<LifeCycleValue>) => (
              <ChooseLifecycle
                value={value}
                onChange={onChange}
              />
            ),
            fullWidth: true,
            type: "confirmOrCancelButton",
            initialValue: {
              lifeCycle: formState.lifeCycle,
              completed: formState.completed,
              reason: formState.lifeCycleReason,
            },
            submitChange: async (values: LifeCycleValue) => updateDocument({
              lifeCycle: values.lifeCycle,
              lifeCycleReason: values.lifeCycle.value === "pending" ? null : values.reason,
              completed: values.lifeCycle.value === "pending" ? null : values.completed
            })
          }
        }, {
          key: "currencyAndExchangeRate",
          label: RESX.Financial.Currency,
          render: () => <CurrencyAndExchangeRate
            bookkeeping={bookkeeping}
            currency={getCurrency(formState.currency)}
            exchangeRate={formState.exchangeRate}
          />,
          inPageEdit: {
            render: ({ value, onChange, cancel }: RenderEditOptions<ICurrencyAndExchangeRate>) => <CurrencyAndExchangeRatePicker
              focusOnMount
              validatorKey="currencyAndExchangeRate"
              bookkeeping={bookkeeping}
              currency={value.currency}
              exchangeRate={value.exchangeRate}
              onChange={onChange}
              onEsc={() => cancel()}
              onEnter={(v) => onChange(v, true)}
            />,
            fullWidth: true,
            type: "confirmOrCancelButton",
            initialValue: { currency: formState.currency, exchangeRate: formState.exchangeRate },
            submitChange: async (values: ICurrencyAndExchangeRate) => updateDocument({
              currency: values.currency,
              exchangeRate: values.exchangeRate
            }),
          },
          editDisabled: isLocked,
        }, {
          key: "bankAccount",
          label: RESX.BankAccount.resxBankAccount,
          hidden: !isInvoice,
          render: () => {
            if (formState.bankAccount != null) {
              return <BankAccountSmallDetails bankAccount={formState.bankAccount} />;
            }
            if (bankAccountsApi.isFetching || bankAccountsApi.isLoading) {
              return <Loading />;
            }
            if (bankAccountsApi.data.length === 0) {
              return <NoBankAccountsMessageBox color="blue" />;
            }
            return <div />;
          },
          isEmpty: !formState.bankAccount && !(bankAccountsApi.isFetching || bankAccountsApi.isLoading) && bankAccountsApi.data.length > 0,
          inPageEdit: {
            render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BankAccountSmall>) => {
              return <BankAccountDropdownOrWarning
                bankAccountQuery={bankAccountsApi}
                bookkeepingId={bookkeeping.bookkeepingId}
                value={value}
                onChange={(ba) => {
                  onChange(ba, true);
                }}
                inPageEditing={{ cancel }}
              />;
            },
            fullWidth: true,
            type: "custom",
            initialValue: formState.bankAccount,
            submitChange: async (bankAccount: server.dto.BankAccountSmall) => updateDocument({ bankAccount })
          },
          editDisabled: isLocked || (bankAccountsApi.data.length === 0 && formState.bankAccount == null),
        }, {
          key: isInvoice ? "invoiceDate" : "sendDate",
          label: RESX.DateTime.SendDate,
          render: () => <DocumentSendDate
            sendDate={formState.sendDate}
            lifeCycle={formState.lifeCycle?.value}
            hasProducts={props.products.collection.length > 0}
          />,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) =>
              <DocumentSendDatePicker
                value={value}
                onChange={onChange}
                dueInDays={formState.dueInDays}
                minDate={moment.utc(minSendDate)}
                maxDate={moment.utc(maxSendDate)}
                inPageEdit={{
                  cancel,
                  submit
                }}
              />,
            type: "confirmOrCancelButtonInline",
            initialValue: formState.sendDate,
            submitChange: (sendDate: moment.Moment) => updateDocument({ sendDate }),
          },
          editDisabled: isLocked,
        }, {
          key: "dueDate",
          label: RESX.DateTime.DueDate,
          render: () => <DocumentDueDate
            dueDate={formState.sendDate}
            dueInDays={formState.dueInDays}
            lifeCycle={formState.lifeCycle?.value}
            hasProducts={props.products.collection.length > 0}
          />,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<number>) =>
              <DocumentDueDatePicker
                sendDate={formState.sendDate}
                dueInDays={value}
                onChange={(_, dueInDays, fromCalendar) => onChange(dueInDays, fromCalendar)}
                inPageEdit={{
                  cancel,
                  submit
                }}
              />,
            type: "confirmOrCancelButtonInline",
            initialValue: formState.dueInDays,
            submitChange: (dueInDays: number) => updateDocument({ dueInDays }),
          },
          editDisabled: isLocked,
        }, {
          key: "paymentTerm",
          label: RESX.DateTime.Term,
          render: () => <DocumentV1Term dueInDays={formState.dueInDays} />,
          inPageEdit: {
            render: ({ value, onChange, cancel }: RenderEditOptions<number>) => {
              return <RequiredNumberInput
                autoFocus
                width={60}
                validatorKey="dueInDays"
                value={value}
                onChange={(v: number) => onChange(v)}
                onEnterPressed={(v: number) => onChange(v, true)}
                onEscPressed={() => cancel()}
                decimalPoints={0}
                labelContent={RESX.DateTime.days}
                minValue={0}
                maxValue={serverReference.constants.invoice.maxDueInDays}
              />;
            },
            type: "confirmOrCancelButton",
            initialValue: formState.dueInDays,
            submitChange: (dueInDays: number) => updateDocument({ dueInDays })
          },
          editDisabled: isLocked,
        }, {
          key: "addressee",
          label: RESX.Document.Addressee,
          render: () => <CompanySmallDetails company={formState.addresseeCompany} onChange={() => props.refetchDocument()} layout="multiLine" />,
          inPageEdit: {
            render: ({ onChange }) => <div className="main-section-template-fields-padding">
              <CompanyModalPicker
                startOpen
                value={null}
                onChange={v => onChange(v, true)}
                modalTitle={RESX.Company.resxCompany}
                addressType="administrative"
                validatorKey="addressee"
                required={false}
                canCreateNew={site.permissions.addressBook.canCreate}
                layout="multiLine"
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: null,
            submitChange: async (company: server.dto.CompanySmall) => updateDocument({
              addresseeCompany: company,
              contact: undefined as unknown as server.dto.ContactSmallV2
            })
          },
          editDisabled: isLocked,
        }, {
          key: "contact",
          label: RESX.Contact.resxContact,
          isEmpty: contact == null,
          render: () => <ContactSmallDetails contact={contact} />,
          inPageEdit: {
            render: ({ value, onChange }) => <div className="main-section-template-fields-padding">
              <ChooseContact
                company={formState.addresseeCompany}
                selectedContact={value}
                onContactChanged={(contact) => onChange(contact, true)}
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: formState.contact,
            submitChange: async (contact: server.dto.ContactSmallV2) => updateDocument({ contact })
          },
          editDisabled: isLocked,
        }, {
          key: "euVatNumber",
          hidden: isInvoice || !props.euVatNumber.isEuMember,
          label: RESX.VatNumber.EuVatNumber,
          render: () => <VatNumberDetails key={props.euVatNumber?.number} euVatNumber={props.euVatNumber} companyId={formState.addresseeCompany?.companyId} />,
        }, {
          key: "concerning",
          label: isInvoice ? RESX.InvoiceDocument.Concerning : RESX.Document.Subject,
          isEmpty: !formState.subject,
          render: () => <OptionalTextAreaDetails>{formState.subject}</OptionalTextAreaDetails>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
              focusOnMount
              maxLength={serverReference.constants.document.maxConcerningLength}
              value={value}
              onChange={onChange}
              onCtrlEnter={submit}
              onEsc={cancel}
            />,
            fullWidth: true,
            type: "confirmOrCancelButton",
            initialValue: formState.subject,
            submitChange: (concerning: string) => updateDocument({ subject: concerning }),
          },
          editDisabled: isLocked,
        }, {
          key: "costCenter",
          label: RESX.CostCenter.resxCostCenter,
          hidden: !isInvoice || !site.currentEnvironment.hasCostCenters,
          isEmpty: !formState.costCenter,
          render: () => <CostCenterLabel value={formState.costCenter} />,
          inPageEdit: {
            render: ({ onChange }) => <div className="main-section-template-fields-padding">
              <CostCenterModalPicker
                canCreateNew={false}
                startOpen
                value={null}
                onChange={v => onChange(v, true)}
                validatorKey="costCenter"
                required={false}
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: formState.costCenter,
            submitChange: async (costCenter: server.dto.CostCenter) => updateDocument({ costCenter })
          },
          editDisabled: isLocked,
        }, {
          key: "status",
          hidden: !hasMultipleStatuses,
          label: RESX.Status.resxStatus,
          render: () => <Status status={formState.status} />,
          inPageEdit: {
            render: ({ value, onChange }) => (
              <StatusDropdown
                value={value}
                onChange={(v) => onChange(v, true)}
                mainArea={documentType}
                options={undefined}
                config={{
                  focusOnMount: true,
                  openMenuOnFocus: true
                }}
              />
            ),
            type: "confirmOrCancelButton",
            initialValue: formState.status,
            fullWidth: true,
            submitChange: (value: server.dto.StatusSmall) => updateDocument({ status: value })
          }
        }, {
          key: "artistRider",
          label: RESX.ArtistRider.resxArtistRider,
          render: () => <ButtonText style={{ padding: 0 }} onClick={downloadArtistRider} label={formState.artistRider.title} />,
          isEmpty: !formState.artistRider,
          hidden: documentType !== "contract" || props.artistRiders.length === 0,
          inPageEdit: {
            render: ({ onChange, value }) => (<ArtistRidersDropdown
              value={value}
              onChange={(v) => onChange(v, true)}
              options={props.artistRiders}
              config={{ openMenuOnFocus: true, focusOnMount: true }}
            />),
            type: "confirmOrCancelButton",
            initialValue: formState.artistRider,
            fullWidth: true,
            submitChange: (artistRider: server.dto.ArtistRider) => updateDocument({ artistRider })
          }
        }, {
          key: "owner",
          label: RESX.GeneralLabels.Owner,
          render: () => <UserWithAvatar user={formState.owner} />,
          inPageEdit: {
            render: ({ onChange }) => <div className="main-section-template-fields-padding">
              <UserModalPicker
                startOpen
                value={null}
                onChange={v => onChange(v, true)}
                validatorKey="owner"
                required={false}
                canCreateNew={false}
              />
            </div>,
            fullWidth: false,
            type: "custom",
            initialValue: formState.owner,
            submitChange: async (owner: server.dto.UserSmall) => updateDocument({ owner }),
          },
          editDisabled: isLocked,
        }]
      },
      {
        key: "reverseCharge",
        title: RESX.ReverseCharge.xReverseCharge,
        type: "regular",
        hidden: !isInvoice || props.euVatNumber == null || props.reverseChargeAdvice == null,
        fields: [{
          key: "euVatNumber",
          label: RESX.VatNumber.EuVatNumber,
          hidden: !props.euVatNumber?.isEuMember,
          render: () => <VatNumberDetails key={props.euVatNumber?.number} euVatNumber={props.euVatNumber} companyId={formState.addresseeCompany?.companyId} />,
        }, {
          key: "reverseChargeAdvice",
          label: RESX.ReverseCharge.Advice,
          render: () => <ReverseChargeAdvice advice={props.reverseChargeAdvice} />,
        }]
      },
      recordInformation(
        document.createdUtc,
        document.owner,
        document.lastModifiedUtc,
        props.lastModifiedUser
      )
    ]}
  />;
};
